import type { MarkerRecord } from '../types'

type MarkerClickHandler = (marker: MarkerRecord) => void

interface MarkerEl {
  el: HTMLElement
  marker: MarkerRecord
}

interface MarkerPlacement {
  left: number
  top: number
  visible: boolean
}

const MARKER_SIZE = 26

export class AnnotationMarkers {
  private container: HTMLElement
  private markers: Map<string, MarkerEl> = new Map()

  constructor(private readonly onClick: MarkerClickHandler) {
    this.container = document.createElement('div')
    Object.assign(this.container.style, {
      position: 'fixed',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '2147483645',
    })
    this.container.setAttribute('data-visual-qa', 'markers')
    const root = document.getElementById('visual-qa-root') ?? document.body
    root.appendChild(this.container)
  }

  upsert(marker: MarkerRecord, index: number): void {
    const existing = this.markers.get(marker.id)
    if (existing) {
      existing.marker = marker
      existing.el.textContent = String(index)
      existing.el.dataset.markerId = marker.id
      this.updateStyle(existing.el, marker)
      this.position(existing.el, marker)
      return
    }

    const el = document.createElement('div')
    el.style.pointerEvents = 'all'
    el.textContent = String(index)
    el.dataset.markerId = marker.id
    this.updateStyle(el, marker)
    this.position(el, marker)
    el.addEventListener('click', (event) => {
      event.stopPropagation()
      this.onClick(this.markers.get(marker.id)?.marker ?? marker)
    })
    this.container.appendChild(el)
    this.markers.set(marker.id, { el, marker })
  }

  private resolvedPlacement(marker: MarkerRecord): MarkerPlacement | null {
    if (!marker.selectorPath) return null
    try {
      const target = document.querySelector(marker.selectorPath)
      if (!(target instanceof Element)) return null
      const rect = target.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return null
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const anchorXRatio = marker.anchorXRatio != null ? marker.anchorXRatio : 0.5
      const anchorYRatio = marker.anchorYRatio != null ? marker.anchorYRatio : 0.5
      const anchorX = rect.left + rect.width * anchorXRatio
      const anchorY = rect.top + rect.height * anchorYRatio
      const visible = anchorX >= 0 && anchorX <= viewportWidth && anchorY >= 0 && anchorY <= viewportHeight
      return {
        left: Math.max(4, Math.min(viewportWidth - MARKER_SIZE - 4, anchorX - MARKER_SIZE / 2)),
        top: Math.max(4, Math.min(viewportHeight - MARKER_SIZE - 4, anchorY - MARKER_SIZE / 2)),
        visible,
      }
    } catch {
      return null
    }
  }

  private position(el: HTMLElement, marker: MarkerRecord): void {
    const placement = this.resolvedPlacement(marker)
    if (placement) {
      el.style.display = ''
      el.style.left = `${placement.left}px`
      el.style.top = `${placement.top}px`
      el.dataset.anchorState = 'resolved'
      el.classList.toggle('offscreen', !placement.visible)
      return
    }

    el.style.display = ''
    el.classList.remove('offscreen')
    el.dataset.anchorState = 'fallback'
    el.style.left = `${marker.x - window.scrollX - 13}px`
    el.style.top = `${marker.y - window.scrollY - 27}px`
  }

  private updateStyle(el: HTMLElement, marker: MarkerRecord): void {
    const classes = ['ik-marker']
    if (marker.status === 'closed') classes.push('dismissed')
    if (marker.active) classes.push('active')
    el.className = classes.join(' ')
    el.title = marker.title.slice(0, 80)
  }

  reposition(markers: MarkerRecord[]): void {
    markers.forEach(marker => {
      const existing = this.markers.get(marker.id)
      if (!existing) return
      existing.marker = marker
      this.position(existing.el, marker)
    })
    this.spread()
  }

  private spread(): void {
    const placed: { left: number; top: number }[] = []
    for (const { el } of this.markers.values()) {
      if (el.style.display === 'none') continue
      const left = parseFloat(el.style.left) || 0
      let top = parseFloat(el.style.top) || 0
      let guard = 0
      while (guard < 16 && placed.some(p => Math.abs(p.left - left) < MARKER_SIZE && Math.abs(p.top - top) < MARKER_SIZE)) {
        top += MARKER_SIZE - 4
        guard++
      }
      el.style.top = `${top}px`
      placed.push({ left, top })
    }
  }

  remove(markerId: string): void {
    const marker = this.markers.get(markerId)
    if (!marker) return
    marker.el.remove()
    this.markers.delete(markerId)
  }

  sync(markers: MarkerRecord[]): void {
    const nextIds = new Set(markers.map(marker => marker.id))
    for (const id of this.markers.keys()) {
      if (!nextIds.has(id)) this.remove(id)
    }
    markers.forEach((marker, index) => this.upsert(marker, index + 1))
    this.spread()
  }

  setVisible(visible: boolean): void {
    this.container.style.display = visible ? '' : 'none'
  }

  clear(): void {
    for (const { el } of this.markers.values()) el.remove()
    this.markers.clear()
  }

  destroy(): void {
    this.container.remove()
    this.markers.clear()
  }
}
